import { useEffect, useRef, useState } from "react";
import {
  Box, Typography, Paper, Button, Stack, Grid2 as Grid, TextField, Alert, MenuItem,
  Table, TableContainer, TableHead, TableRow, TableCell, TableBody, IconButton, Divider,
  CircularProgress, Chip, Checkbox, Tooltip,
  Tabs, Tab, Dialog, DialogContent, DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PostAddIcon from "@mui/icons-material/PostAdd";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import PlaceIcon from "@mui/icons-material/Place";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import LockClockIcon from "@mui/icons-material/LockClock";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  salesInvoicesApi, branchesApi, itemPickerApi, itemsApi, billingOffersApi, customersApi,
  medicationOrderQueueApi, stockApi,
  type ItemDto, type CustomerDto, type ConfusableDto, type ServiceDto,
  type StockAvailabilityDto,
  type CreateSalesInvoiceRequest, type CreateSalesInvoiceLineRequest,
  type LineType, type PaymentLineRequest, type SubstituteOptionDto,
  type MedicationOrderBillingPrefill,
  type QuoteLineRequest, type PriceSource, type QuotedPackageDto,
  tenantsApi, consultantsApi,
} from "@/api/endpoints";
import { PackagesPanel, toPackageRequests, type SelectedPackage } from "@/features/sales/PackagesPanel";
import { useAuth } from "@/store/auth";
import axios from "axios";
import { errorMessage, type ApiProblem } from "@/api/client";
import { ItemPicker, CustomerPicker } from "@/components/Pickers";
import { ServicePicker } from "@/components/ServicePicker";
import { GenericInfoButton } from "@/components/GenericInfoButton";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { lasaConflictsFrom } from "@/components/LasaConfirmDialog";
import {
  NEAR_EXPIRY_DAYS, allocateFefo, byFefo, daysToExpiry, dispensable,
  expiryLabel, expiryState, pickDefaultBatch,
} from "@/shared/batches";
import { SubstitutePanel } from "./SubstitutePanel";
import { formatDate, todayIso } from "@/shared/datetime";
import { useBillingDimensions } from "@/shared/hooks/useBillingDimensions";

interface DraftLine {
  key: string;
  lineType: LineType;
  item: ItemDto | null;
  service?: ServiceDto | null;
  serviceId?: string;
  description?: string;
  prescriptionRef?: string;
  qty: number;
  rate: number;
  taxAmount: number;
  discountAmount: number;
  substitutedFromItemId?: string;
  substituteReasonCode?: string;
  /** Pharmacist accepted the swap. The API rejects the line without it when the pair demands one. */
  substituteConfirmed?: boolean;
  /** Batches of this item holding stock in the chosen warehouse, earliest expiry first. */
  batches: StockAvailabilityDto[];
  /** The batch being dispensed. Required by the API for every stock line. */
  batchId: string;
  batchesLoading?: boolean;
  /** Where the server priced this line — shown so the counter can see why a rate is what it is. */
  priceSource?: PriceSource;
  /** Required once this line's discount passes the standard ceiling (W5). */
  discountReason?: string;
  /**
   * Whether the payer covers this line (W19). Only meaningful once a scheme is on the bill.
   * Starts covered — the biller marks the exclusions, reading the agreement's own words.
   */
  isPayerCovered: boolean;
  /** W21 — required before a Schedule H1 or X line can be billed. */
  prescriberName?: string;
  prescriberRegNo?: string;
  /**
   * W22 — the promotion the server applied, echoed back on the quote. Read-only here: the offer
   * is the hospital's, not something the counter types or can talk itself out of.
   */
  offerName?: string;
  offerDiscount?: number;
}

/**
 * W21 — whether this line's drug needs a prescriber on the statutory register. H1 and X do;
 * plain Schedule H is prescription-only but carries no separate register requirement, and
 * prompting on every one of those would train pharmacists to type anything into the box.
 */
const needsPrescriber = (l: DraftLine) =>
  ["H1", "X"].includes((l.item?.scheduleCategory ?? "").trim().toUpperCase());

interface DraftPayment {
  key: string;
  mode: PaymentLineRequest["mode"];
  amountReceived: number;
  reference?: string;
}

/** Why a line costs what it does, in the counter's language rather than the enum's. */
const PRICE_SOURCE_LABEL: Record<PriceSource, string> = {
  ServiceCharge: "Rate card",
  ServiceRate: "Rate history",
  ServiceDefault: "Service default",
  PriceList: "Price list",
  BatchMrp: "Batch MRP",
  ItemDefaultMrp: "Item MRP",
  FreeText: "Manual",
  Package: "Package share",
};

const newLine = (lineType: LineType = "Item"): DraftLine => ({
  key: crypto.randomUUID(), lineType, item: null,
  qty: 1, rate: 0, taxAmount: 0, discountAmount: 0,
  batches: [], batchId: "",
  isPayerCovered: true,
});

const newPayment = (mode: PaymentLineRequest["mode"] = "Cash"): DraftPayment => ({
  key: crypto.randomUUID(), mode, amountReceived: 0,
});

/** Compose the description sub-line shown in the cart: form · packaging · strength. */
function describeItem(item: { form?: string; packaging?: string; strength?: string } | null): string {
  if (!item) return "";
  return [item.form, item.packaging, item.strength].filter(Boolean).join(" · ");
}

/**
 * More batches than this for one item usually means a mistyped quantity rather than a genuine
 * dispense, so the split asks for confirmation first.
 */
const MAX_SPLIT_BATCHES = 4;

const LINE_TYPES: LineType[] = ["Rx", "Item", "Service", "Other"];
const PAYMENT_MODES: PaymentLineRequest["mode"][] = ["Cash", "Card", "Upi", "BankTransfer", "Cheque", "Credit"];
const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

export function CreateSalesInvoicePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  // Two hand-offs land on this screen via location.state:
  //   * PrescriptionQueuePage → { medicationOrder } — HealNTrack Rx, billed OP.
  //   * IpBillingPage        → { billType, uhid, customerName, admissionId, wardCode } — an
  //     admitted patient. This screen is the only billing form, so it must honour the bill
  //     type it was handed; posting everything as OutPatient mislabels every IP bill.
  const handoff = location.state as {
    medicationOrder?: MedicationOrderBillingPrefill;
    billType?: CreateSalesInvoiceRequest["billType"];
    uhid?: string; customerName?: string; admissionId?: string; wardCode?: string;
  } | null;
  const prefill = handoff?.medicationOrder;
  // Frozen at mount: the bill type is decided by how the cashier got here, not editable mid-bill.
  const [billType] = useState<CreateSalesInvoiceRequest["billType"]>(
    handoff?.billType === "InPatient" ? "InPatient" : "OutPatient");
  const isInPatient = billType === "InPatient";
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  // The invoice posted but the prescription could not be closed out (e.g. an unmapped line →
  // 422 UNMAPPED_LINE). Surfaced instead of silently swallowed: the order stays in the queue and
  // needs attention, and we must not navigate away as if nothing happened.
  const [completionWarning, setCompletionWarning] = useState<string | null>(null);

  const [customer, setCustomer] = useState<CustomerDto | null>(null);
  const [customerName, setCustomerName] = useState<string>("");
  const [customerPhone, setCustomerPhone] = useState<string>("");

  // Billing dimensions (W1). These decide which charge-master row prices the bill, so they are
  // captured on the header before any line is added. "" means "not set" — the base rate.
  const [admissionTypeId, setAdmissionTypeId] = useState<string>("");
  const [personTypeId, setPersonTypeId] = useState<string>("");
  const [insuranceSchemeId, setInsuranceSchemeId] = useState<string>("");
  const [insurancePolicyNo, setInsurancePolicyNo] = useState<string>("");
  const dimensions = useBillingDimensions();
  const [warehouseId, setWarehouseId] = useState<string>("");
  const [documentDate, setDocumentDate] = useState<string>(todayIso);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [notes, setNotes] = useState<string>("");
  /** Why the bill-level discount was given. Required past the standard ceiling (W5). */
  const [discountReason, setDiscountReason] = useState<string>("");
  const [activeTab, setActiveTab] = useState<LineType>("Item");
  const [lines, setLines] = useState<DraftLine[]>([newLine("Item")]);
  // W18 — packages on this bill. Held separately from the lines because they are not lines: the
  // server expands each into several, and the counter never sets the rates that come back.
  const [selectedPackages, setSelectedPackages] = useState<SelectedPackage[]>([]);
  const [quotedPackages, setQuotedPackages] = useState<QuotedPackageDto[]>([]);
  /** W19 — the payer's share of this bill, as the server computes it. Zero on a self-pay bill. */
  const [quotedPayerPayable, setQuotedPayerPayable] = useState(0);
  /** W24 — the rounding the server will apply. Zero unless the tenant has a rule. */
  const [quotedRoundOff, setQuotedRoundOff] = useState(0);
  /**
   * W20 — the attending consultant. Every service line inherits it, so a bill where one doctor did
   * everything is one choice rather than nine.
   */
  const [consultantId, setConsultantId] = useState("");
  const [payments, setPayments] = useState<DraftPayment[]>([newPayment()]);
  const [substituteFor, setSubstituteFor] = useState<{ lineKey: string; item: ItemDto; qty: number } | null>(null);
  const [barcode, setBarcode] = useState<string>("");
  // LASA (look-alike/sound-alike) — Feedback batch 1, Code 8. Per-line confusable partners
  // drive a warning banner (Low/Medium) or a blocking confirmation (High).
  const [lasaByLine, setLasaByLine] = useState<Record<string, ConfusableDto[]>>({});
  const [lasaConfirm, setLasaConfirm] = useState<{ lineKey: string; itemName: string; confusables: ConfusableDto[] } | null>(null);
  // Items whose High-severity warning the cashier accepted. Sent on Submit: the server blocks the
  // dispense without them and audits the acceptance, so the confirmation is a record, not just a
  // dialog that was dismissed.
  const [lasaAcknowledged, setLasaAcknowledged] = useState<Record<string, true>>({});

  /** Fetch confusables for the item just placed on a line; warn (or block, on High). */
  const checkLasa = (lineKey: string, item: ItemDto | null) => {
    setLasaByLine(m => { const n = { ...m }; delete n[lineKey]; return n; });
    if (!item) return;
    // A previous acceptance does not carry over to a newly chosen item — the cashier must
    // confirm the drug now in their hand.
    setLasaAcknowledged(m => { const n = { ...m }; delete n[item.id]; return n; });
    itemsApi.confusables(item.id)
      .then(cf => {
        if (cf.length === 0) return;
        setLasaByLine(m => ({ ...m, [lineKey]: cf }));
        if (cf.some(c => c.severity === "High"))
          setLasaConfirm({ lineKey, itemName: item.name, confusables: cf });
      })
      .catch(() => { /* missing permission or network — the server gate still blocks the dispense */ });
  };

  const branches = useQuery({
    queryKey: ["branches"], queryFn: () => branchesApi.list(), staleTime: 5 * 60_000,
  });

  // The UHID that identifies the patient on this bill, whichever hand-off brought us here.
  // Carrying it through matters beyond the printed bill: discharge reconciliation finds a
  // patient's open charges by UHID, so a bill posted without one is invisible at discharge.
  const patientUhid = handoff?.uhid ?? prefill?.uhid ?? customer?.uhid ?? undefined;

  // Resolve the handed-over UHID to a real customer so the bill links to the patient record
  // rather than posting as an anonymous walk-in. Falls back to the name as free text.
  const uhidHandoffApplied = useRef(false);
  useEffect(() => {
    const uhid = handoff?.uhid?.trim();
    if (!uhid || uhidHandoffApplied.current) return;
    uhidHandoffApplied.current = true;
    setCustomerName(handoff?.customerName ?? "");
    customersApi.list({ search: uhid, pageSize: 5 })
      .then(r => {
        const match = r.items.find(c => c.uhid?.trim().toLowerCase() === uhid.toLowerCase());
        if (match) { setCustomer(match); setCustomerName(""); }
      })
      .catch(() => { /* no customer record yet — the walk-in name keeps the bill identifiable */ });
  }, [handoff?.uhid, handoff?.customerName]);

  // Seed the billing dimensions from whoever the bill is for. Keyed on the customer's id, not the
  // object, so it re-seeds when a different patient is picked but never overwrites what the
  // biller typed afterwards for this one. A walk-in (no customer) clears back to the base rate.
  const seededForCustomerId = useRef<string | null>(null);
  useEffect(() => {
    const id = customer?.id ?? null;
    if (seededForCustomerId.current === id) return;
    seededForCustomerId.current = id;
    setAdmissionTypeId(customer?.admissionTypeId ?? "");
    setPersonTypeId(customer?.personTypeId ?? "");
    setInsuranceSchemeId(customer?.insuranceSchemeId ?? "");
    setInsurancePolicyNo(customer?.insurancePolicyNo ?? "");
  }, [customer]);

  // Apply the prescription-queue prefill exactly once: customer + Rx lines. Mapped lines get
  // a partial ItemDto (id + display fields) and the order qty/MRP; unmapped lines keep the
  // brand name in the description so the pharmacist picks the catalog item manually.
  const prefillApplied = useRef(false);
  useEffect(() => {
    if (!prefill || prefillApplied.current) return;
    prefillApplied.current = true;
    setPendingOrderId(prefill.id);
    setActiveTab("Rx");
    const rxRef = `HN-${prefill.medicationOrderId}`;

    setLines(prefill.lines.map((l): DraftLine => ({
      key: crypto.randomUUID(),
      lineType: "Rx",
      prescriptionRef: rxRef,
      item: l.itemId ? ({
        id: l.itemId, sku: l.itemSku ?? "", name: l.itemName ?? l.brandName ?? "",
        genericName: l.genericName, itemType: "Medicine", baseUomId: "", costingMethod: "FEFO",
        reorderLevel: 0, reorderQty: 0, safetyStock: 0, leadTimeDays: 0,
        isBatchTracked: true, isExpiryTracked: true,
        form: l.form, strength: l.strength, defaultMrp: l.mrp, gstRate: 0, isScheduleDrug: false,
      } as ItemDto) : null,
      description: l.itemId ? undefined : (l.brandName ?? `HNT product #${l.medicationId}`),
      qty: l.qty, rate: l.mrp ?? 0, taxAmount: 0, discountAmount: 0,
      // Batches resolve once the pharmacy is picked — the warehouse effect handles it.
      batches: [], batchId: "",
      isPayerCovered: true,
    })));

    if (prefill.customerId) {
      customersApi.get(prefill.customerId).then(setCustomer).catch(() => { /* ignore; UHID header still shown */ });
    }
  }, [prefill]);

  const updateLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  /**
   * Load the dispensable batches for a line and pre-select one by FEFO. Stock lines cannot be
   * billed without a batch (the API rejects them), so this runs on every path that puts an item
   * on a line: the picker, a barcode scan, a substitution, and a change of warehouse.
   */
  const loadBatches = async (key: string, item: ItemDto | null, qty: number, wh = warehouseId) => {
    if (!item || !wh) {
      updateLine(key, { batches: [], batchId: "", batchesLoading: false });
      return;
    }
    updateLine(key, { batchesLoading: true });
    try {
      const rows = (await stockApi.availability({ itemId: item.id, warehouseId: wh }))
        .filter(b => b.availableQty > 0)
        .sort(byFefo);
      updateLine(key, { batches: rows, batchId: pickDefaultBatch(rows, qty), batchesLoading: false });
    } catch {
      // Never block billing on the lookup — the cashier still sees "no batch" and the
      // validation line below stops the post.
      updateLine(key, { batches: [], batchId: "", batchesLoading: false });
    }
  };
  // Batches are warehouse-specific: switching the dispensing pharmacy invalidates every
  // selection on the cart, so all stock lines are re-resolved against the new warehouse.
  const linesRef = useRef<DraftLine[]>(lines);
  useEffect(() => { linesRef.current = lines; });
  useEffect(() => {
    linesRef.current.forEach(l => { if (l.item) loadBatches(l.key, l.item, l.qty, warehouseId); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId]);

  /**
   * Expand one line into as many lines as the quantity needs, walking batches FEFO. The cashier
   * types 35 once; if the earliest batch holds 20 this produces a 20 line and a 15 line, which is
   * exactly what they would otherwise have keyed by hand — one batch per line is what the API
   * reserves against.
   */
  const splitLineAcrossBatches = (key: string) => {
    const line = linesRef.current.find(l => l.key === key);
    if (!line) return;
    const { plan, shortfall } = allocateFefo(line.batches, line.qty);
    if (plan.length === 0) return;
    if (plan.length > MAX_SPLIT_BATCHES
      && !confirm(`${line.item?.name} needs ${plan.length} batches to cover ${line.qty}. `
        + "Check the quantity is right — split anyway?")) return;

    const keys: string[] = [];
    setLines(ls => {
      const idx = ls.findIndex(l => l.key === key);
      if (idx < 0) return ls;
      const split = plan.map((p, i) => {
        const k = i === 0 ? key : crypto.randomUUID();
        keys.push(k);
        return { ...ls[idx], key: k, batchId: p.batchId, qty: p.qty };
      });
      // Anything that could not be covered stays on a trailing line with no batch, so the
      // shortfall is visible rather than silently dropped from the bill.
      if (shortfall > 0) {
        const k = crypto.randomUUID();
        keys.push(k);
        split.push({ ...ls[idx], key: k, batchId: "", qty: shortfall });
      }
      return [...ls.slice(0, idx), ...split, ...ls.slice(idx + 1)];
    });
    // Carry the LASA warning onto every line the split produced — same item, same risk.
    setLasaByLine(m => {
      const cf = m[key];
      if (!cf) return m;
      const next = { ...m };
      keys.forEach(k => { next[k] = cf; });
      return next;
    });
  };

  const removeLine = (key: string) => {
    setLines((ls) => ls.filter((l) => l.key !== key));
    setLasaByLine((m) => { const n = { ...m }; delete n[key]; return n; });
  };
  const addLine = () => setLines((ls) => [...ls, newLine(activeTab)]);

  /** UC-M1-SAL-07: scanner-input box. On Enter, resolve the SKU and append a stock-policy=Issue line. */
  const scanBarcode = async () => {
    const code = barcode.trim();
    if (!code || !warehouseId) return;
    try {
      const row = await itemPickerApi.byBarcode(code);
      // Build an ItemDto compatible with the existing line model.
      const itemLike: ItemDto = {
        id: row.id, sku: row.sku, name: row.name, genericName: row.genericName,
        genericNameId: row.genericNameId,
        itemType: "Medicine", baseUomId: "", costingMethod: "FEFO",
        reorderLevel: 0, reorderQty: 0, safetyStock: 0, leadTimeDays: 0,
        isBatchTracked: true, isExpiryTracked: true,
        form: row.form, packaging: row.packaging, strength: row.strength,
        manufacturer: row.manufacturer, category: row.category,
        defaultMrp: row.mrp, gstRate: 0, isScheduleDrug: row.isScheduleDrug,
      };
      const scanned = { ...newLine(activeTab), item: itemLike, rate: row.mrp ?? 0 };
      setLines((ls) => [
        ...ls.filter(l => !(l.lineType === activeTab && l.item === null)),
        scanned,
      ]);
      setBarcode("");
      checkLasa(scanned.key, itemLike);
      loadBatches(scanned.key, itemLike, scanned.qty);
    } catch (e) {
      // Surface error inline near the scan field — picker already shows toast for 404.
      console.warn("barcode lookup failed", e);
    }
  };

  const updatePayment = (key: string, patch: Partial<DraftPayment>) =>
    setPayments((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  const removePayment = (key: string) => setPayments((ps) => ps.filter((p) => p.key !== key));
  const addPayment = () => setPayments((ps) => [...ps, newPayment()]);

  const handleSubstituteSelect = (sub: SubstituteOptionDto, confirmed: boolean) => {
    if (!substituteFor) return;
    const substituteItem = {
      id: sub.itemId, sku: sub.sku, name: sub.name, genericName: sub.genericName,
      genericNameId: sub.genericNameId,
      form: sub.form, strength: sub.strength, manufacturer: sub.manufacturer,
      itemType: "Medicine", baseUomId: "", costingMethod: "MovingAverage",
      reorderLevel: 0, reorderQty: 0, safetyStock: 0, leadTimeDays: 0,
      isBatchTracked: true, isExpiryTracked: true,
      gstRate: 0, isScheduleDrug: false,
      defaultMrp: sub.mrp,
    } as ItemDto;
    // Replace the line's item with the substitute
    updateLine(substituteFor.lineKey, {
      // We don't have a full ItemDto for the substitute. Construct a partial one
      // adequate for the picker display. The backend doesn't need our local item state
      // for validation — only the itemId.
      item: substituteItem,
      substitutedFromItemId: substituteFor.item.id,
      substituteReasonCode: sub.reasonCode,
      substituteConfirmed: confirmed,
      rate: sub.mrp ?? 0,
      // The old item's batches belong to a different molecule — drop them and re-resolve.
      batches: [], batchId: "",
    });
    loadBatches(substituteFor.lineKey, substituteItem, substituteFor.qty);
    // A substitution changes the drug being dispensed, so it needs the same confusable check as
    // picking the item by hand — this was the one path that changed the item without one.
    checkLasa(substituteFor.lineKey, substituteItem);
    setSubstituteFor(null);
  };

  // W2/W3 — the server prices every line, so the counter asks it what the bill comes to instead of
  // working it out locally. Without this the screen would quote the catalog default or the batch
  // MRP while the invoice was raised at the patient's contracted rate: the cashier would collect
  // the wrong amount and then be unable to post, because the payment no longer covered the bill.
  //
  // Keyed on the inputs that actually change a price. A line's own `rate` is excluded — the quote
  // writes it — except on free-text lines, where it is the input and comes back unchanged.
  const packageRequests = toPackageRequests(selectedPackages);

  const pricingKey = JSON.stringify({
    customerId: customer?.id ?? null,
    billType, documentDate,
    admissionTypeId, personTypeId, insuranceSchemeId,
    lines: lines.map(l => [
      l.lineType, l.item?.id ?? null, l.serviceId ?? null, l.batchId || null,
      l.qty, l.discountAmount, l.lineType === "Other" ? l.rate : 0,
      // W19 — unmarking coverage moves money between the two payables without changing any rate,
      // so it has to re-quote or the counter would ask the patient for the wrong figure.
      l.isPayerCovered,
    ]),
    // A changed delivered quantity can move the package price, so it belongs in the key.
    packages: packageRequests,
    // The bill discount is apportioned across both sides, so it moves the split too.
    discountAmount,
  });

  const [quoting, setQuoting] = useState(false);
  /** Non-blocking notes from the server's quote — currently mixed batch MRPs (W6). */
  const [priceWarnings, setPriceWarnings] = useState<string[]>([]);
  useEffect(() => {
    const priceable = lines.some(l => l.item?.id || l.serviceId || l.lineType === "Other")
      || packageRequests.length > 0;
    if (!priceable) return;

    let cancelled = false;
    const handle = setTimeout(() => {
      const payload: QuoteLineRequest[] = lines.map((l, idx) => ({
        lineType: l.lineType,
        itemId: l.item?.id ?? null,
        serviceId: l.serviceId ?? null,
        batchId: l.batchId || null,
        qty: l.qty,
        discountAmount: l.discountAmount,
        rate: l.rate,
        lineNo: idx,
        isPayerCovered: l.isPayerCovered,
      }));
      setQuoting(true);
      salesInvoicesApi.quote({
        customerId: customer?.id ?? null,
        billType,
        lines: payload,
        admissionTypeId: admissionTypeId || null,
        personTypeId: personTypeId || null,
        insuranceSchemeId: insuranceSchemeId || null,
        on: documentDate,
        packages: packageRequests.length > 0 ? packageRequests : undefined,
        // Sent so the split accounts for it — a bill discount comes off both sides in proportion.
        headerDiscount: discountAmount,
      })
        .then(res => {
          if (cancelled) return;
          setPriceWarnings(res.warnings ?? []);
          setQuotedPackages(res.packages ?? []);
          setQuotedPayerPayable(res.payerPayable ?? 0);
          setQuotedRoundOff(res.roundOff ?? 0);
          const byLineNo = new Map(res.lines.map(q => [q.lineNo, q]));
          setLines(ls => ls.map((l, idx) => {
            const q = byLineNo.get(idx);
            if (!q) return l;
            // Tax is only overwritten where the pricing source states a rate. A stock item's MRP
            // is tax-inclusive, so anything the cashier typed there is left alone — the server
            // makes exactly the same distinction when it raises the bill.
            const servicePriced = q.source === "ServiceCharge"
              || q.source === "ServiceRate" || q.source === "ServiceDefault";
            return {
              ...l,
              rate: q.rate,
              taxAmount: servicePriced ? q.taxAmount : l.taxAmount,
              priceSource: q.source,
              // W22 — what the promotion took off, so the counter can tell the patient. Not
              // written into discountAmount: that field is the cashier's, and the server adds the
              // offer to it when the bill is raised.
              offerName: q.offerName ?? undefined,
              offerDiscount: q.offerDiscount,
            };
          }));
        })
        .catch(() => { /* keep the last known rates; create will still price authoritatively */ })
        .finally(() => { if (!cancelled) setQuoting(false); });
    }, 250);

    return () => { cancelled = true; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingKey]);

  // W20 — who can be named as the attending. Only the active ones: a bill raised against somebody
  // who has left would earn them a share nobody is going to pay.
  const consultants = useQuery({
    queryKey: ["consultants", "billing-options"],
    queryFn: () => consultantsApi.list({ isActive: true, pageSize: 200 }),
    staleTime: 5 * 60_000,
  });

  // W4 — how much this user may give away. Mirrors the server's tiering: the elevated ceiling for
  // a holder of sales.discount.override, the standard one otherwise.
  const tenant = useQuery({
    queryKey: ["tenant", "current"], queryFn: tenantsApi.current, staleTime: 5 * 60_000,
  });
  const canOverrideDiscount = useAuth(s => s.hasPermission("sales.discount.override"));
  const standardDiscountPct = tenant.data?.standardDiscountLimitPct ?? 10;
  const discountLimitPct = canOverrideDiscount
    ? tenant.data?.elevatedDiscountLimitPct ?? 30
    : standardDiscountPct;

  /** A discount on a zero-value line can't be a proportion; the server treats it as total too. */
  const discountPct = (gross: number, discount: number) =>
    discount <= 0 ? 0 : gross <= 0 ? 100 : (discount / gross) * 100;

  // W18 — a package's own lines are not in `lines`; the server holds them until the bill is
  // raised. Their value comes back on the quote, and has to reach the total the cashier collects.
  const packageSubTotal = quotedPackages.reduce(
    (s, p) => s + p.lines.reduce((t, l) => t + l.amount, 0), 0);
  const packageTax = quotedPackages.reduce(
    (s, p) => s + p.lines.reduce((t, l) => t + l.taxAmount, 0), 0);

  const subTotal = lines.reduce((s, l) => s + l.qty * l.rate, 0) + packageSubTotal;
  const taxTotal = lines.reduce((s, l) => s + l.taxAmount, 0) + packageTax;

  const insured = !!insuranceSchemeId;
  // W22 — the offers the server applied are part of the bill's discount, and the invoice will
  // carry them in the same figure. Counting only what the cashier typed would quote a total the
  // patient is not asked for.
  const lineDisc = lines.reduce((s, l) => s + l.discountAmount + (l.offerDiscount ?? 0), 0);
  // W24 — the rounding the server will apply, so the counter asks for the figure the invoice will
  // carry rather than one that differs by up to a rupee.
  const netAmount = subTotal + taxTotal - lineDisc - discountAmount + quotedRoundOff;

  // W19 — the split comes back on the quote, computed by the same routine that will record it.
  // Working it out here as well would be a second implementation of "what does the patient owe",
  // which is the exact problem this change set has been removing since W2.
  const payerPayable = insured ? quotedPayerPayable : 0;
  const patientPayable = netAmount - payerPayable;

  const totalReceived = payments.reduce((s, p) => s + p.amountReceived, 0);
  // What the counter is collecting now. On an insured bill that is the patient's share only.
  const balanceDue = Math.max(0, patientPayable - totalReceived);
  const changeToReturn = Math.max(0, totalReceived - netAmount);

  /** Mockup §UX_WORKFLOWS §4 — one-tap "Fill balance on Cash" for counter speed. */
  const fillBalanceOnCash = () => {
    if (balanceDue <= 0) return;
    setPayments((ps) => {
      const cashIdx = ps.findIndex(p => p.mode === "Cash");
      if (cashIdx >= 0) {
        const next = [...ps];
        next[cashIdx] = { ...next[cashIdx], amountReceived: next[cashIdx].amountReceived + balanceDue };
        return next;
      }
      return [...ps, { ...newPayment("Cash"), amountReceived: balanceDue }];
    });
  };

  /** Rx/Item lines that will be reserved when Submit is called (UC-M1-SAL-06 preview chip). */
  const reservationPreview = lines.filter(l => (l.lineType === "Rx" || l.lineType === "Item") && l.item);

  /**
   * Stock shortfalls per item, totalled across every line and every batch. Reporting this at item
   * level rather than per batch is the difference between "only 15 in batch B-7" — which sends the
   * cashier hunting batch by batch — and "30 requested, 25 available, 5 short".
   */
  const shortfalls = (() => {
    const byItem = new Map<string, { name: string; demand: number; available: number; keys: string[] }>();
    if (!warehouseId) return [];
    for (const l of lines) {
      if (!l.item || (l.lineType !== "Rx" && l.lineType !== "Item")) continue;
      // Batches still loading would read as zero available and flash a false shortage.
      if (l.batchesLoading) return [];
      const prev = byItem.get(l.item.id);
      if (prev) {
        prev.demand += l.qty;
        prev.keys.push(l.key);
      } else {
        byItem.set(l.item.id, {
          name: l.item.name,
          demand: l.qty,
          // Availability is a property of the item in this warehouse, not of the line — every
          // line for the same item sees the same batch list, so it is counted once.
          available: l.batches.filter(dispensable).reduce((s, b) => s + b.availableQty, 0),
          keys: [l.key],
        });
      }
    }
    return [...byItem.entries()]
      .filter(([, v]) => v.demand > v.available)
      .map(([itemId, v]) => ({ itemId, ...v, short: v.demand - v.available }));
  })();

  /**
   * Partial dispense — the normal counter outcome when stock is short. Trims the item's lines to
   * what is actually on the shelf and lays the result out across batches FEFO, so the cashier can
   * bill what they can hand over now instead of clearing the line and starting again.
   */
  const billAvailableNow = (itemId: string) => {
    const target = shortfalls.find(s => s.itemId === itemId);
    if (!target) return;
    const first = linesRef.current.find(l => l.key === target.keys[0]);
    if (!first) return;
    const { plan } = allocateFefo(first.batches, target.available);

    setLines(ls => {
      const out: DraftLine[] = [];
      for (const l of ls) {
        if (l.key === target.keys[0]) {
          // The item's whole demand collapses into the FEFO plan, in place.
          plan.forEach((p, i) => out.push({
            ...l,
            key: i === 0 ? l.key : crypto.randomUUID(),
            batchId: p.batchId,
            qty: p.qty,
          }));
        } else if (!target.keys.includes(l.key)) {
          out.push(l);
        }
        // this item's remaining lines are folded into the plan above — dropped
      }
      return out;
    });
  };

  /**
   * Reorder the cart by bin code so a multi-line bill is picked in one pass down the aisle instead
   * of criss-crossing the pharmacy. Lines with no bin mapped sort last — they need looking up, and
   * putting them at the end keeps the walk uninterrupted. Non-stock lines (Service / Other) hold
   * their position at the bottom, since nothing is picked for them.
   */
  const binOf = (l: DraftLine) => l.batches.find(b => b.batchId === l.batchId)?.binCode ?? "";
  const canSortByBin = lines.filter(l => binOf(l)).length > 1;
  const sortByBin = () => {
    setLines(ls => [...ls].sort((a, b) => {
      const aStock = (a.lineType === "Rx" || a.lineType === "Item") && !!a.item;
      const bStock = (b.lineType === "Rx" || b.lineType === "Item") && !!b.item;
      if (aStock !== bStock) return aStock ? -1 : 1;
      const ab = binOf(a), bb = binOf(b);
      if (!ab) return bb ? 1 : 0;
      if (!bb) return -1;
      return ab.localeCompare(bb, undefined, { numeric: true, sensitivity: "base" });
    }));
  };

  const validate = (): string | null => {
    if (!warehouseId) return "Pick a warehouse";
    // W18 — a bill of packages alone is legitimate: a health check is often the whole visit.
    if (lines.length === 0 && selectedPackages.length === 0)
      return "Add at least one line or package";

    // Item-level shortage first: it explains the whole problem in one sentence, where the
    // per-line checks below would only report whichever batch happens to be selected.
    const s = shortfalls[0];
    if (s) return `${s.name}: ${s.demand} requested, only ${s.available} available — short by ${s.short}`;
    for (const [i, l] of lines.entries()) {
      if (l.lineType !== "Other" && l.lineType !== "Service" && !l.item)
        return `Line ${i + 1}: item not selected`;
      if (l.lineType === "Service" && !l.serviceId) return `Line ${i + 1}: service not selected`;
      if (l.lineType === "Other" && !l.description) return `Line ${i + 1}: description required`;
      if (l.lineType === "Rx" && !l.prescriptionRef) return `Line ${i + 1}: Rx reference required`;

      // W21 — a Schedule H1 or X supply goes on a register an inspector can ask for. The server
      // refuses the bill without these; stopping here saves the cashier a round trip at a queue.
      if (needsPrescriber(l)) {
        const cat = l.item?.scheduleCategory;
        if (!l.prescriberName?.trim())
          return `Line ${i + 1}: ${l.item?.sku} is Schedule ${cat} — the register needs the prescriber's name`;
        if (!l.prescriberRegNo?.trim())
          return `Line ${i + 1}: ${l.item?.sku} is Schedule ${cat} — the register needs the prescriber's registration number`;
        if (!l.prescriptionRef?.trim())
          return `Line ${i + 1}: ${l.item?.sku} is Schedule ${cat} — record the prescription reference`;
      }
      if (l.qty <= 0) return `Line ${i + 1}: qty must be > 0`;
      if (l.rate < 0) return `Line ${i + 1}: rate must be >= 0`;

      // Stock lines dispense a specific batch — the API rejects them without one, and
      // expired stock must never leave the counter.
      if ((l.lineType === "Rx" || l.lineType === "Item") && l.item) {
        if (l.batches.length === 0) return `Line ${i + 1}: no stock for ${l.item.name} in this pharmacy`;
        if (!l.batchId) return `Line ${i + 1}: pick a batch`;
        const b = l.batches.find(x => x.batchId === l.batchId);
        if (b) {
          const d = daysToExpiry(b.expiryDate);
          if (d !== null && d < 0)
            return `Line ${i + 1}: batch ${b.batchNo} expired ${expiryLabel(b.expiryDate)} — pick another`;
          if (l.qty > b.availableQty)
            return `Line ${i + 1}: only ${b.availableQty} available in batch ${b.batchNo}`
              + ` — split across batches or reduce the quantity`;
        }
      }
    }
    // W4 — the same two checks the server runs, so the cashier is stopped before they collect
    // payment rather than after. The server is still the authority; this only saves the round trip.
    for (const [i, l] of lines.entries()) {
      const linePct = discountPct(l.qty * l.rate, l.discountAmount);
      if (linePct > discountLimitPct)
        return `Line ${i + 1}: ${linePct.toFixed(1)}% discount is over your ${discountLimitPct}% limit`;
      // W5 — past the everyday ceiling the concession has to say why, even for an approver.
      if (linePct > standardDiscountPct && !l.discountReason?.trim())
        return `Line ${i + 1}: a discount above ${standardDiscountPct}% needs a reason`;
    }
    const billPct = discountPct(subTotal, lineDisc + discountAmount);
    if (billPct > discountLimitPct)
      return `Bill discount of ${billPct.toFixed(1)}% is over your ${discountLimitPct}% limit`;
    if (discountPct(subTotal, discountAmount) > standardDiscountPct && !discountReason.trim())
      return `A bill discount above ${standardDiscountPct}% needs a reason`;

    if (netAmount > 0 && totalReceived === 0 && payments.every(p => p.mode !== "Credit"))
      return "Add at least one payment (or use Credit mode)";
    return null;
  };

  /**
   * S3.1/S3.4 workflow: Create draft → fetch RowVersion → Submit (with If-Match) →
   * Post with payment table. The Submit hop reserves stock and surfaces the
   * BIL-002 422 + substitutesOffered payload when an Rx/Item line is OOS.
   */
  const mutation = useMutation({
    meta: { successMessage: "Sales invoice created" },
    mutationFn: async () => {
      const cmd: CreateSalesInvoiceRequest = {
        customerId: customer?.id ?? undefined,
        customerName: customer ? undefined : (customerName || undefined),
        customerPhone: customer ? undefined : (customerPhone || undefined),
        billType,
        uhid: patientUhid,
        admissionId: isInPatient ? handoff?.admissionId : undefined,
        // Sent explicitly rather than left to the server's customer fallback: what is on screen is
        // what the biller agreed with the patient, including any override of their usual scheme.
        admissionTypeId: admissionTypeId || null,
        personTypeId: personTypeId || null,
        insuranceSchemeId: insuranceSchemeId || null,
        insurancePolicyNo: insuranceSchemeId ? (insurancePolicyNo.trim() || null) : null,
        warehouseId,
        documentDate,
        discountAmount,
        discountReason: discountReason.trim() || null,
        notes: notes || undefined,
        lines: lines.map((l, idx): CreateSalesInvoiceLineRequest => ({
          lineType: l.lineType,
          itemId: l.item?.id,
          serviceId: l.serviceId,
          batchId: l.batchId || undefined,
          description: l.description,
          prescriptionRef: l.prescriptionRef,
          substitutedFromItemId: l.substitutedFromItemId,
          substituteReasonCode: l.substituteReasonCode,
          substituteConfirmed: l.substituteConfirmed ?? false,
          qty: l.qty, rate: l.rate, taxAmount: l.taxAmount, discountAmount: l.discountAmount,
          discountReason: l.discountReason?.trim() || null,
          // W19 — what the agreement does not cover is the patient's in full, whatever the co-pay.
          isPayerCovered: l.isPayerCovered,
          // W21 — the statutory register entry. Refused server-side on an H1 or X line without it.
          prescriberName: l.prescriberName?.trim() || null,
          prescriberRegNo: l.prescriberRegNo?.trim() || null,
          lineNo: idx + 1,
        })),
        packages: packageRequests.length > 0 ? packageRequests : undefined,
        // W20 — who performed the work. Service lines inherit it server-side.
        consultantId: consultantId || null,
      };
      const created = await salesInvoicesApi.create(cmd);
      const detail = await salesInvoicesApi.get(created.id) as { rowVersion: string; documentNo?: string };
      await salesInvoicesApi.submit(created.id, detail.rowVersion, Object.keys(lasaAcknowledged));

      const paymentPayload: PaymentLineRequest[] = payments
        .filter(p => p.amountReceived > 0 || p.mode === "Credit")
        .map(p => ({ mode: p.mode, amountReceived: p.amountReceived, reference: p.reference || undefined }));
      await salesInvoicesApi.post(created.id, paymentPayload);

      // Close the loop with HealNTrack: mark the prescription billed + write status back. The bill
      // itself is already posted, so a failure here does not fail the sale — but it is NOT silent:
      // a 422 UNMAPPED_LINE means the order was NOT marked billed and stays in the queue (double-bill
      // risk), so we carry the message back and warn the user instead of navigating away.
      let completionError: string | null = null;
      if (pendingOrderId) {
        try { await medicationOrderQueueApi.complete(pendingOrderId, created.id, detail.documentNo); }
        catch (e) { completionError = errorMessage(e); }
      }
      return { created, documentNo: detail.documentNo, completionError };
    },
    onSuccess: ({ documentNo, completionError }) => {
      qc.invalidateQueries({ queryKey: ["sales-invoices"] });
      if (completionError) {
        // Invoice posted, prescription not closed out — keep the user here with a clear warning.
        setCompletionWarning(
          `Invoice ${documentNo ?? ""} was posted, but the prescription could not be closed out: `
          + `${completionError} The order remains in the prescription queue and must be resolved `
          + `(map the item or wait for the item-sync job), or it may be billed again.`);
      } else {
        navigate("/sales/invoices");
      }
    },
    onError: (err) => { pivotToSubstitutes(err); surfaceLasaBlock(err); },
  });

  /**
   * The server refused the dispense because a High-severity LASA item was not confirmed. Normally
   * the dialog has already been shown at item-pick time and this never fires — but it does when the
   * confusables lookup failed, when the pair was added after the line was keyed, or when the item
   * arrived by a path that skipped the check. Re-open the confirmation from the server's own list
   * so the cashier can resolve it without losing the bill.
   */
  function surfaceLasaBlock(err: unknown) {
    const conflicts = lasaConflictsFrom(err);
    const first = conflicts?.[0];
    if (!first) return;

    const line = linesRef.current.find(l => l.item?.id === first.itemId);
    setLasaConfirm({
      lineKey: line?.key ?? "",
      itemName: first.itemName,
      confusables: conflicts!
        .filter(c => c.itemId === first.itemId)
        .map(c => ({
          partnerItemId: c.partnerItemId, partnerSku: c.partnerSku, partnerName: c.partnerName,
          confusionType: c.confusionType, severity: c.severity, notes: c.notes,
        })),
    });
  }

  /**
   * BIL-002 — Submit reserves stock, so a line can still go out of stock between the cashier
   * loading batches and posting (another counter selling the last pack). The server answers 422
   * with the offending item and a substitute list; without this the cashier just sees a red
   * message. Open the substitute panel on the affected line so the next step is one click.
   */
  function pivotToSubstitutes(err: unknown) {
    if (!axios.isAxiosError<ApiProblem>(err)) return;
    if (err.response?.status !== 422) return;
    const data = err.response.data;
    if (data?.code !== "INSUFFICIENT_STOCK") return;
    const details = data.errors as { itemId?: string; available?: number } | undefined;
    if (!details?.itemId) return;

    const line = linesRef.current.find(l => l.item?.id === details.itemId);
    if (!line?.item) return;
    setSubstituteFor({ lineKey: line.key, item: line.item, qty: line.qty });
    // The batch list we hold is now stale — whoever took the stock did so after we loaded it.
    loadBatches(line.key, line.item, line.qty);
  }

  const validationError = validate();
  const showSubstitutePanel = !!substituteFor && !!warehouseId;

  return (
    <Box sx={{ display: "flex", gap: 0, height: "calc(100vh - 96px)" }}>
      {/* minWidth:0 — without it this flex item can't shrink below the cart table's natural
          width and the whole page scrolls sideways. The tables scroll inside themselves instead. */}
      <Box sx={{ flex: 1, minWidth: 0, overflow: "auto", pr: showSubstitutePanel ? 2 : 0 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          {isInPatient ? "In-patient billing" : "Outpatient billing"}
        </Typography>
        {/* Persistent, not dismissible: the cashier must be able to see at any point in the
            bill which mode they are in and who it is for. */}
        {isInPatient && (
          <Alert severity="info" icon={<LocalHospitalIcon fontSize="inherit" />} sx={{ mb: 2 }}
            data-testid="billing-ip-banner">
            <b>In-patient bill</b>
            {patientUhid ? <> · UHID <b>{patientUhid}</b></> : null}
            {handoff?.customerName ? <> · {handoff.customerName}</> : null}
            {handoff?.wardCode ? <> · Ward {handoff.wardCode}</> : null}
            {handoff?.admissionId ? <> · Admission {handoff.admissionId}</> : null}
          </Alert>
        )}
        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="billing-server-error">
              {errorMessage(mutation.error)}
            </Alert>
          )}
          {completionWarning && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="billing-completion-warning"
              action={<Button color="inherit" size="small"
                onClick={() => navigate("/sales/invoices")}>Go to invoices</Button>}>
              {completionWarning}
            </Alert>
          )}
          {/* W6 — the same medicine on two lines at two prices is legitimate (stock arrives in
              waves) but the patient will ask. The counter should hear it from us first. */}
          {priceWarnings.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="billing-price-warning">
              {priceWarnings.map((w, i) => <div key={i}>{w}</div>)}
            </Alert>
          )}

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Patient</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomerPicker value={customer} onChange={setCustomer} label="Existing customer" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" color="text.secondary">
                — or fill walk-in details below —
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField label="Walk-in name" size="small" fullWidth
                  value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                  disabled={!!customer} />
                <TextField label="Walk-in phone" size="small" sx={{ width: 180 }}
                  value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)}
                  disabled={!!customer} />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} label="Pharmacy / warehouse *"
                size="small" fullWidth value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                <option value="">— Pick branch —</option>
                {branches.data?.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField label="Bill date *" type="date" size="small" fullWidth
                value={documentDate} onChange={(e) => setDocumentDate(e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            {/* Billing basis — the three dimensions the charge master prices against. Seeded from
                the patient record; override here when this visit differs from their usual terms. */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                label="Admission type" size="small" fullWidth
                value={admissionTypeId} onChange={(e) => setAdmissionTypeId(e.target.value)}
                helperText="Blank bills at the standard rate">
                <option value="">— Standard —</option>
                {dimensions.admissionTypes.map((d) => (
                  <option key={d.id} value={d.id}>{d.code} — {d.name}</option>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                label="Person type" size="small" fullWidth
                value={personTypeId} onChange={(e) => setPersonTypeId(e.target.value)}
                helperText="Staff, VIP, camp — concessional categories">
                <option value="">— None —</option>
                {dimensions.personTypes.map((d) => (
                  <option key={d.id} value={d.id}>{d.code} — {d.name}</option>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                label="Insurance scheme" size="small" fullWidth
                value={insuranceSchemeId}
                onChange={(e) => {
                  setInsuranceSchemeId(e.target.value);
                  // A policy number belongs to the plan it was issued under; keeping it across a
                  // scheme change would quote a number from the wrong payer.
                  if (!e.target.value) setInsurancePolicyNo("");
                }}
                helperText="Blank = self-pay">
                <option value="">— Self-pay —</option>
                {dimensions.insuranceSchemes.map((d) => (
                  <option key={d.id} value={d.id}>{d.code} — {d.name}</option>
                ))}
              </TextField>
            </Grid>
            {insuranceSchemeId && (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField label="Policy / member no." size="small" fullWidth
                  value={insurancePolicyNo}
                  onChange={(e) => setInsurancePolicyNo(e.target.value)}
                  inputProps={{ maxLength: 100 }} />
              </Grid>
            )}
            {/* W20 — who performed the work. Every service line inherits it, so a bill where one
                doctor did everything is one choice rather than nine. */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                label="Attending consultant" size="small" fullWidth
                value={consultantId} onChange={(e) => setConsultantId(e.target.value)}
                helperText={consultantId
                  ? "Service lines earn them a share; stock lines do not"
                  : "Blank = nobody earns a share on this bill"}>
                <option value="">— None —</option>
                {(consultants.data?.items ?? []).map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.specialty ? ` · ${c.specialty}` : ""}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField type="number" label="Header discount (₹)" size="small" fullWidth
                value={discountAmount} onChange={(e) => setDiscountAmount(Number(e.target.value) || 0)}
                error={discountPct(subTotal, lineDisc + discountAmount) > discountLimitPct}
                helperText={`You may discount up to ${discountLimitPct}% of the bill`}
                inputProps={{ min: 0, step: "0.01" }} />
            </Grid>
            {discountPct(subTotal, discountAmount) > standardDiscountPct && (
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField label="Discount reason *" size="small" fullWidth
                  value={discountReason} onChange={(e) => setDiscountReason(e.target.value)}
                  error={!discountReason.trim()}
                  helperText={`Required above ${standardDiscountPct}% — recorded against your name on the bill`}
                  inputProps={{ maxLength: 500 }} />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <TextField label="Notes" size="small" fullWidth value={notes}
                onChange={(e) => setNotes(e.target.value)} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* UC-M1-SAL-05 offers / scheme banners — sourced from GET /billing-offers/active. */}
          <BillingOfferBanners />


          {/* Mockup §5 — Rx | Item | Service | Other tabs drive the default line type. */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v as LineType)}
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            data-testid="billing-line-type-tabs"
          >
            {LINE_TYPES.map(t => (
              <Tab
                key={t} label={t} value={t}
                sx={{ minHeight: 48, fontWeight: 600 }}
                data-testid={`billing-tab-${t.toLowerCase()}`}
              />
            ))}
          </Tabs>

          {/* UC-M1-SAL-07 — barcode scan input (counter wedge or camera). Enter to add line. */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
            <QrCodeScannerIcon color="action" />
            <TextField
              size="small"
              placeholder="Scan or type a barcode (GTIN / EAN-13)"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scanBarcode(); } }}
              sx={{ flex: 1, maxWidth: 360 }}
              disabled={!warehouseId}
              inputProps={{ "data-testid": "billing-barcode-input" }}
            />
            <Button
              onClick={scanBarcode} disabled={!warehouseId || !barcode.trim()} size="small"
              data-testid="billing-barcode-add"
            >
              Add by scan
            </Button>
          </Stack>

          {/* One line per short item, totalled across batches, with the partial-dispense action
              right next to it — the counter's actual decision is "bill what we have". */}
          {shortfalls.map(s => (
            <Alert
              key={s.itemId} severity="warning" sx={{ mb: 1 }}
              data-testid={`billing-shortfall-${s.itemId}`}
              action={s.available > 0 ? (
                <Button color="inherit" size="small" onClick={() => billAvailableNow(s.itemId)}
                  data-testid={`billing-bill-available-${s.itemId}`}>
                  Bill {s.available} now
                </Button>
              ) : undefined}
            >
              <b>{s.name}</b> — {s.demand} requested, only {s.available} available across all
              batches in this pharmacy. Short by {s.short}.
            </Alert>
          ))}

          {/* W18 — packages sit above the lines because that is the order a counter works in:
              "the executive health check, plus a consultation". They bill as lines too, but the
              server expands them, so they are not editable here as lines. */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Packages</Typography>
            {selectedPackages.length > 0 && (
              <Chip size="small" variant="outlined"
                label={`${selectedPackages.length} on this bill`} />
            )}
          </Stack>
          <Box sx={{ mb: 2 }}>
            <PackagesPanel
              selected={selectedPackages}
              onChange={setSelectedPackages}
              quoted={quotedPackages}
              insuranceSchemeId={insuranceSchemeId}
              documentDate={documentDate}
              disabled={mutation.isPending}
            />
          </Box>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">Lines</Typography>
              {reservationPreview.length > 0 && (
                <Chip
                  icon={<LockClockIcon />}
                  size="small"
                  color="info"
                  variant="outlined"
                  label={`${reservationPreview.length} line(s) will be reserved on Submit`}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              {canSortByBin && (
                <Button startIcon={<LowPriorityIcon />} onClick={sortByBin} size="small"
                  data-testid="billing-sort-by-bin">
                  Pick order
                </Button>
              )}
              <Button startIcon={<AddIcon />} onClick={addLine} size="small">
                Add {activeTab} line
              </Button>
            </Stack>
          </Stack>

          {/* The cart needs ~950px for its columns to stay readable; on a narrower screen it
              scrolls horizontally within this container rather than off the page edge. */}
          <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 1180 }} data-testid="billing-line-cart">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 110 }}>Type *</TableCell>
                <TableCell sx={{ minWidth: 280 }}>Item / Service *</TableCell>
                <TableCell sx={{ width: 230 }}>Batch · expiry *</TableCell>
                <TableCell sx={{ width: 120 }}>Rx ref / Desc</TableCell>
                <TableCell align="right" sx={{ width: 80 }}>Qty *</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Rate (₹)</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Tax</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Disc</TableCell>
                <TableCell align="right" sx={{ width: 110 }}>Total</TableCell>
                {/* W19 — only on an insured bill. On a self-pay one there is nobody to cover
                    anything, and a column of always-ticked boxes would be noise. */}
                {insured && (
                  <TableCell align="center" sx={{ width: 90 }}>
                    <Tooltip title="Untick what the agreement does not cover — it becomes the patient's in full">
                      <span>Covered</span>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody data-testid="billing-line-cart-body">
              {lines.map((l, idx) => {
                // W22 — the offer comes off too. The server adds it to the line's discount when
                // the bill is raised, so the counter has to show the same total or the cashier
                // collects a figure the invoice disagrees with.
                const offerOff = l.offerDiscount ?? 0;
                const lineTotal = l.qty * l.rate + l.taxAmount - l.discountAmount - offerOff;
                const needsItem = l.lineType === "Rx" || l.lineType === "Item";
                return (
                  <TableRow
                    key={l.key}
                    data-testid={`billing-line-row-${idx}`}
                    data-line-type={l.lineType}
                  >
                    <TableCell>
                      <TextField select size="small" value={l.lineType}
                        onChange={(e) => updateLine(l.key, {
                          lineType: e.target.value as LineType, item: null, service: null,
                          serviceId: undefined, description: "", batches: [], batchId: "",
                        })}
                        sx={{ width: 100 }}>
                        {LINE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {needsItem ? (
                        <Stack spacing={0.25}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ItemPicker value={l.item} onChange={(v) => {
                              const r = v?.defaultMrp ?? 0;
                              updateLine(l.key, { item: v, rate: l.rate || r, batches: [], batchId: "" });
                              checkLasa(l.key, v);
                              loadBatches(l.key, v, l.qty);
                            }} label={`Line ${idx + 1}`} required error={!l.item} />
                            {l.substitutedFromItemId && (
                              <Chip size="small" color="warning" label="Substituted" icon={<SwapHorizIcon />} />
                            )}
                            <GenericInfoButton
                              genericNameId={l.item?.genericNameId}
                              genericName={l.item?.genericName}
                            />
                          </Stack>
                          {l.item && describeItem(l.item) && (
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                              {describeItem(l.item)}
                              {l.substitutedFromItemId && substituteFor?.item && (
                                <> · Substituted for {substituteFor.item.name}</>
                              )}
                            </Typography>
                          )}
                          {lasaByLine[l.key]?.length > 0 && (
                            <Alert
                              severity={lasaByLine[l.key].some(c => c.severity === "High") ? "error" : "warning"}
                              icon={<WarningAmberIcon fontSize="inherit" />}
                              sx={{ py: 0, px: 1, "& .MuiAlert-message": { py: 0.25 } }}
                              data-testid={`billing-lasa-warning-${idx}`}
                            >
                              LASA: easily confused with{" "}
                              {lasaByLine[l.key].map(c => c.partnerName).join(", ")}
                            </Alert>
                          )}
                        </Stack>
                      ) : l.lineType === "Service" ? (
                        <ServicePicker value={l.service ?? null}
                          onChange={(v) => {
                            const gross = (l.qty || 1) * (v?.currentRate ?? 0);
                            updateLine(l.key, {
                              service: v, serviceId: v?.id,
                              description: v ? `${v.code} — ${v.name}` : "",
                              rate: v?.currentRate ?? l.rate,
                              taxAmount: v ? Math.round(gross * (v.gstRate ?? 0)) / 100 : l.taxAmount,
                            });
                          }}
                          label={`Line ${idx + 1}`} required error={!l.serviceId} />
                      ) : (
                        <TextField placeholder="Free-text description" size="small" fullWidth
                          value={l.description ?? ""} onChange={(e) => updateLine(l.key, { description: e.target.value })} />
                      )}
                    </TableCell>
                    <TableCell>
                      <BatchCell
                        line={l}
                        warehousePicked={!!warehouseId}
                        onChange={(batchId) => updateLine(l.key, { batchId })}
                        onSplit={() => splitLineAcrossBatches(l.key)}
                        testId={`billing-line-batch-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      {/* An H1 or X drug needs the reference whatever line type it was billed as:
                          the register has to point at a prescription, and the rule does not care
                          which button the cashier pressed (W21). */}
                      {(l.lineType === "Rx" || needsPrescriber(l)) && (
                        <TextField placeholder="Rx #" size="small" value={l.prescriptionRef ?? ""}
                          onChange={(e) => updateLine(l.key, { prescriptionRef: e.target.value })}
                          error={needsPrescriber(l) && !l.prescriptionRef?.trim()}
                          sx={{ width: 110 }} />
                      )}
                      {needsPrescriber(l) && (
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          <Chip size="small" color="warning" variant="outlined"
                            label={`Schedule ${l.item?.scheduleCategory}`} sx={{ height: 18 }} />
                          <TextField placeholder="Prescriber *" size="small"
                            value={l.prescriberName ?? ""}
                            onChange={(e) => updateLine(l.key, { prescriberName: e.target.value })}
                            error={!l.prescriberName?.trim()}
                            sx={{ width: 110 }} />
                          <TextField placeholder="Reg. no. *" size="small"
                            value={l.prescriberRegNo ?? ""}
                            onChange={(e) => updateLine(l.key, { prescriberRegNo: e.target.value })}
                            error={!l.prescriberRegNo?.trim()}
                            sx={{ width: 110 }} />
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={l.qty}
                        onChange={(e) => updateLine(l.key, { qty: Number(e.target.value) || 0 })}
                        inputProps={{ min: 0, step: "1", style: { textAlign: "right" }, "data-testid": `billing-line-qty-${idx}` }} sx={{ width: 70 }} />
                    </TableCell>
                    <TableCell align="right">
                      {/* Catalog lines are priced by the server (W2/W3), so the rate is shown
                          rather than typed — an editable box the server overwrites is a trap.
                          Discounts still go through the discount column. Free-text lines have no
                          catalog row behind them and stay editable. */}
                      <TextField type="number" size="small" value={l.rate}
                        onChange={(e) => updateLine(l.key, { rate: Number(e.target.value) || 0 })}
                        disabled={l.lineType !== "Other"}
                        helperText={l.priceSource ? PRICE_SOURCE_LABEL[l.priceSource] : undefined}
                        FormHelperTextProps={{ sx: { m: 0, fontSize: "0.6875rem", textAlign: "right" } }}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} sx={{ width: 110 }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={l.taxAmount}
                        onChange={(e) => updateLine(l.key, { taxAmount: Number(e.target.value) || 0 })}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} sx={{ width: 80 }} />
                    </TableCell>
                    <TableCell align="right">
                      {/* W5 — past the everyday ceiling the concession has to say why, and the
                          server records who was signed in when it was approved. Below it, the box
                          stays out of the way: a reason prompt on every ₹2 round-off gets "-". */}
                      {(() => {
                        const pct = discountPct(l.qty * l.rate, l.discountAmount);
                        const needsReason = pct > standardDiscountPct;
                        return (
                          <Stack spacing={0.5} alignItems="flex-end">
                            <TextField type="number" size="small" value={l.discountAmount}
                              onChange={(e) => updateLine(l.key, { discountAmount: Number(e.target.value) || 0 })}
                              error={pct > discountLimitPct}
                              inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} sx={{ width: 80 }} />
                            {needsReason && (
                              <TextField size="small" placeholder="Reason *"
                                value={l.discountReason ?? ""}
                                onChange={(e) => updateLine(l.key, { discountReason: e.target.value })}
                                error={!l.discountReason?.trim()}
                                inputProps={{ maxLength: 500 }} sx={{ width: 160 }} />
                            )}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      <Stack alignItems="flex-end" spacing={0}>
                        <span>{inr.format(lineTotal)}</span>
                        {/* W22 — the promotion, named. A patient asking why the price dropped
                            gets an answer, and the cashier did not have to know it existed. */}
                        {offerOff > 0 && (
                          <Tooltip title={l.offerName ?? "Offer applied"}>
                            <Typography variant="caption" color="success.main">
                              −{inr.format(offerOff)} offer
                            </Typography>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    {insured && (
                      <TableCell align="center">
                        <Checkbox size="small" checked={l.isPayerCovered}
                          onChange={(e) => updateLine(l.key, { isPayerCovered: e.target.checked })} />
                      </TableCell>
                    )}
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {needsItem && l.item && warehouseId && (
                          <IconButton size="small" color="warning"
                            title="Find substitutes for this item"
                            onClick={() => setSubstituteFor({ lineKey: l.key, item: l.item!, qty: l.qty })}>
                            <SwapHorizIcon fontSize="small" />
                          </IconButton>
                        )}
                        {/* The last line normally stays so the table is never empty — but a bill
                            of packages alone is a real bill, and then it has to go. */}
                        <IconButton size="small" onClick={() => removeLine(l.key)}
                          disabled={lines.length === 1 && selectedPackages.length === 0}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </TableContainer>

          <Divider sx={{ my: 3 }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Payments</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={balanceDue <= 0}
                onClick={fillBalanceOnCash}
              >
                Fill ₹{balanceDue.toFixed(2)} on Cash
              </Button>
              <Button startIcon={<AddIcon />} onClick={() => addPayment()} size="small">Add payment</Button>
            </Stack>
          </Stack>
          <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 620 }} data-testid="billing-payments-table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 140 }}>Mode</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Amount (₹)</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell sx={{ width: 60 }} />
              </TableRow>
            </TableHead>
            <TableBody data-testid="billing-payments-body">
              {payments.map((p, pIdx) => (
                <TableRow
                  key={p.key}
                  data-testid={`billing-payment-row-${pIdx}`}
                  data-payment-mode={p.mode}
                >
                  <TableCell>
                    <TextField select size="small" value={p.mode}
                      onChange={(e) => updatePayment(p.key, { mode: e.target.value as PaymentLineRequest["mode"] })}
                      sx={{ width: 130 }}
                      inputProps={{ "data-testid": `billing-payment-mode-${pIdx}` }}>
                      {PAYMENT_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" value={p.amountReceived}
                      onChange={(e) => updatePayment(p.key, { amountReceived: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" }, "data-testid": `billing-payment-amount-${pIdx}` }} sx={{ width: 130 }} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={p.reference ?? ""}
                      placeholder="Card txn ID, UPI ref, cheque #..."
                      onChange={(e) => updatePayment(p.key, { reference: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removePayment(p.key)} disabled={payments.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>

          <Divider sx={{ my: 3 }} />
          {/* Seven totals at spacing 4 don't fit a narrow card — wrap instead of overflowing. */}
          <Stack direction="row" justifyContent="flex-end" flexWrap="wrap" useFlexGap
            columnGap={4} rowGap={2} sx={{ mb: 2 }} data-testid="billing-totals">
            <Stack>
              <Typography variant="caption" color="text.secondary">Subtotal</Typography>
              <Typography data-testid="billing-total-subtotal">{inr.format(subTotal)}</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">Tax</Typography>
              <Typography data-testid="billing-total-tax">{inr.format(taxTotal)}</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">Discount</Typography>
              <Typography data-testid="billing-total-discount">− {inr.format(lineDisc + discountAmount)}</Typography>
            </Stack>
            {/* W24 — shown only when a rule is in force, and named. A total that quietly differs
                from subtotal + tax − discount is the thing a cashier queries at the end of a day. */}
            {quotedRoundOff !== 0 && (
              <Stack>
                <Typography variant="caption" color="text.secondary">Round off</Typography>
                <Typography data-testid="billing-total-roundoff">
                  {quotedRoundOff > 0 ? "+" : "−"}{inr.format(Math.abs(quotedRoundOff))}
                </Typography>
              </Stack>
            )}
            <Stack>
              <Typography variant="caption" color="text.secondary">Net</Typography>
              <Typography variant="h6" color="primary" data-testid="billing-total-net">{inr.format(netAmount)}</Typography>
            </Stack>
            {/* W19 — on an insured bill the net is not what the counter collects. Showing the
                two shares beside it is what stops a cashier asking the patient for the payer's
                money, which is the mistake this whole change prevents. */}
            {payerPayable > 0 && (
              <>
                <Stack>
                  <Typography variant="caption" color="text.secondary">Patient pays</Typography>
                  <Typography variant="h6" data-testid="billing-total-patient">
                    {inr.format(patientPayable)}
                  </Typography>
                </Stack>
                <Stack>
                  <Typography variant="caption" color="text.secondary">Claimed from payer</Typography>
                  <Tooltip title="Becomes a receivable when the bill posts — not collected here">
                    <Typography variant="h6" color="info.main" data-testid="billing-total-payer">
                      {inr.format(payerPayable)}
                    </Typography>
                  </Tooltip>
                </Stack>
              </>
            )}
            <Stack>
              <Typography variant="caption" color="text.secondary">Received</Typography>
              <Typography data-testid="billing-total-received">{inr.format(totalReceived)}</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">
                {payerPayable > 0 ? "Patient balance" : "Balance"}
              </Typography>
              <Typography variant="h6" color={balanceDue > 0 ? "error" : "success.main"} data-testid="billing-total-balance">
                {inr.format(balanceDue)}
              </Typography>
            </Stack>
            {changeToReturn > 0 && (
              <Stack>
                <Typography variant="caption" color="text.secondary">Change to return</Typography>
                <Chip
                  color="success"
                  label={inr.format(changeToReturn)}
                  sx={{ fontSize: "1rem", fontWeight: 600, height: 32 }}
                  data-testid="billing-total-change"
                />
              </Stack>
            )}
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button onClick={() => {
              // If this bill came from a prescription, release the claim back to the queue.
              if (pendingOrderId) {
                medicationOrderQueueApi.release(pendingOrderId).catch(() => { /* stale claim auto-releases */ });
                navigate("/sales/prescription-queue");
              } else {
                navigate("/sales/invoices");
              }
            }} data-testid="billing-cancel">Cancel</Button>
            <Button variant="contained" color="success"
              // Held while a quote is in flight: the totals are about to change, and the payment
              // lines the cashier has keyed are based on them.
              disabled={!!validationError || mutation.isPending || quoting}
              startIcon={mutation.isPending || quoting
                ? <CircularProgress size={16} color="inherit" />
                : <PostAddIcon />}
              onClick={() => mutation.mutate()}
              data-testid="billing-post-invoice">
              {quoting ? "Pricing…" : "Bill & post"}
            </Button>
          </Stack>
          {validationError && (
            <Alert severity="info" sx={{ mt: 2 }} data-testid="billing-validation-error">
              {validationError}
            </Alert>
          )}
        </Paper>
      </Box>

      {showSubstitutePanel && (
        <Box sx={{ width: 380, flexShrink: 0 }}>
          <SubstitutePanel
            primaryItemId={substituteFor!.item.id}
            primaryItemName={substituteFor!.item.name}
            warehouseId={warehouseId}
            requiredQty={substituteFor!.qty}
            onSelect={handleSubstituteSelect}
            onClose={() => setSubstituteFor(null)}
          />
        </Box>
      )}

      {/* High-severity LASA — block until the cashier confirms this is the intended item. */}
      <Dialog open={!!lasaConfirm} onClose={() => setLasaConfirm(null)} maxWidth="xs" fullWidth
        data-testid="billing-lasa-confirm">
        <ClosableDialogTitle onClose={() => setLasaConfirm(null)}>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberIcon color="error" /> <span>Confirm the correct item</span>
          </Stack>
        </ClosableDialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 2 }}>
            <b>{lasaConfirm?.itemName}</b> is a high-risk look-alike / sound-alike item.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>It can be confused with:</Typography>
          <Stack spacing={0.5}>
            {lasaConfirm?.confusables.map(c => (
              <Typography key={c.partnerItemId} variant="body2">
                • <b>{c.partnerSku}</b> · {c.partnerName} <Chip size="small" color="error" label={c.severity} sx={{ ml: 0.5 }} />
              </Typography>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            Double-check the pack before dispensing.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" data-testid="billing-lasa-change"
            onClick={() => {
              if (lasaConfirm) {
                updateLine(lasaConfirm.lineKey, { item: null, batches: [], batchId: "" });
                setLasaByLine(m => { const n = { ...m }; delete n[lasaConfirm.lineKey]; return n; });
              }
              setLasaConfirm(null);
            }}>
            Change item
          </Button>
          <Button variant="contained" color="error" data-testid="billing-lasa-accept"
            onClick={() => {
              // Record the acceptance against the item — it travels to the server on Submit,
              // which is what unblocks the dispense and writes the audit row.
              const itemId = lines.find(l => l.key === lasaConfirm?.lineKey)?.item?.id;
              if (itemId) setLasaAcknowledged(m => ({ ...m, [itemId]: true }));
              setLasaConfirm(null);
            }}>
            Yes, this is correct
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/**
 * Batch + expiry for one cart line. Pharmacy counters dispense by batch, so the cashier needs to
 * see which batch is going out and when it expires before the bill is posted — and the API
 * requires a batch on every stock line regardless.
 *
 * Non-stock lines (Service / Other) and lines without an item render a dash: there is nothing to
 * dispense.
 */
function BatchCell({
  line, warehousePicked, onChange, onSplit, testId,
}: {
  line: DraftLine;
  warehousePicked: boolean;
  onChange: (batchId: string) => void;
  onSplit: () => void;
  testId: string;
}) {
  const needsStock = line.lineType === "Rx" || line.lineType === "Item";
  if (!needsStock) return <Typography variant="caption" color="text.secondary">—</Typography>;
  if (!line.item) {
    return <Typography variant="caption" color="text.secondary">Pick an item first</Typography>;
  }
  if (!warehousePicked) {
    return <Typography variant="caption" color="text.secondary">Pick a pharmacy first</Typography>;
  }
  if (line.batchesLoading) {
    return <Stack direction="row" spacing={1} alignItems="center">
      <CircularProgress size={14} /><Typography variant="caption" color="text.secondary">Loading…</Typography>
    </Stack>;
  }
  if (line.batches.length === 0) {
    return (
      <Typography variant="caption" color="error" data-testid={`${testId}-empty`}>
        No stock in this pharmacy
      </Typography>
    );
  }

  const selected = line.batches.find(b => b.batchId === line.batchId);
  const exp = expiryState(selected?.expiryDate);
  const short = !!selected && selected.availableQty < line.qty;
  // Splitting only helps when the total across batches actually covers the quantity.
  const splitPlan = allocateFefo(line.batches, line.qty);
  const canSplit = short && splitPlan.shortfall === 0 && splitPlan.plan.length > 1;

  return (
    <Stack spacing={0.25}>
      <TextField
        select size="small" fullWidth value={line.batchId}
        onChange={(e) => onChange(e.target.value)}
        error={!line.batchId || exp.color === "error" || short}
        inputProps={{ "data-testid": testId }}
      >
        <MenuItem value=""><em>— pick batch —</em></MenuItem>
        {line.batches.map(b => {
          const d = daysToExpiry(b.expiryDate);
          const expired = d !== null && d < 0;
          return (
            <MenuItem key={b.batchId} value={b.batchId}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <span>{b.batchNo}</span>
                <Typography variant="caption"
                  color={expired ? "error" : d !== null && d <= NEAR_EXPIRY_DAYS ? "warning.main" : "text.secondary"}>
                  {expired ? "EXPIRED" : expiryLabel(b.expiryDate)}
                </Typography>
                <Typography variant="caption" color="text.secondary">· {b.availableQty} avail</Typography>
              </Stack>
            </MenuItem>
          );
        })}
      </TextField>
      {selected && (
        <Typography variant="caption" color={exp.color} data-testid={`${testId}-expiry`}>
          {exp.label}
          {selected.mrp > 0 && ` · MRP ${inr.format(selected.mrp)}`}
        </Typography>
      )}
      {/* Where to walk to. Only shown once a batch is chosen, so the line reads as one
          instruction: this batch, from this bin. */}
      {selected?.binCode && (
        <Stack direction="row" spacing={0.5} alignItems="center" data-testid={`${testId}-bin`}>
          <PlaceIcon sx={{ fontSize: 14 }} color="action" />
          <Typography variant="caption" color="text.secondary">
            Bin <b>{selected.binCode}</b>{selected.binName ? ` · ${selected.binName}` : ""}
          </Typography>
        </Stack>
      )}
      {short && (
        <Stack spacing={0.25}>
          <Typography variant="caption" color="error">
            Only {selected!.availableQty} available in this batch
          </Typography>
          {/* The quantity fits if it is spread over several batches — offer that as one click
              rather than making the cashier add and key the extra lines by hand. */}
          {canSplit && (
            <Button size="small" startIcon={<CallSplitIcon fontSize="small" />} onClick={onSplit}
              sx={{ alignSelf: "flex-start", py: 0 }} data-testid={`${testId}-split`}>
              Split {line.qty} across {splitPlan.plan.length} batches
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * UC-M1-SAL-05 — active billing offers, as notices on the OP billing form.
 *
 * <p><b>W22 changed what these are for.</b> They used to be instructions: the pharmacist read the
 * banner and typed the discount into the line. The server now applies the offer itself, so these
 * say what is running rather than what somebody has to remember to do — and the discount appears
 * against the line, where the patient can see it.</p>
 */
function BillingOfferBanners() {
  const { data, isLoading } = useQuery({
    queryKey: ["billing-offers", "active"],
    queryFn: () => billingOffersApi.active(),
    staleTime: 60_000,
  });

  if (isLoading || !data || data.length === 0) return null;

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {data.map(o => {
        const valueLabel = o.discountType === "Percent"
          ? `${o.discountValue}% off`
          : `₹${o.discountValue} off`;
        const scopeLabel = o.itemName ? ` on ${o.itemName}` : " on eligible items";
        const validity = ` (valid until ${formatDate(o.validTo)})`;
        return (
          <Alert
            key={o.id}
            icon={<LocalOfferIcon fontSize="inherit" />}
            severity="info"
          >
            <b>{o.name}</b> — {valueLabel}{scopeLabel}{validity}
            {o.description ? ` · ${o.description}` : ""}
            <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
              Applied automatically to eligible lines — nothing to type.
            </Typography>
          </Alert>
        );
      })}
    </Stack>
  );
}
